import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { ArrowLeft, MessageSquareText } from "lucide-react";
import "../assets/styles.css";
import { buildConsumerRuntimeHeaders, resolveConsumerRuntimeApiBase } from "../runtime-endpoints";
import { buildInvoiceAccessUrl, readConsumerOrderSession } from "../orderSession";
import { trn } from "../shared/i18n";

const PHONE_SANITIZE_RE = /[^\d+]/g;

function buildKioskHeaders(sellingPointId?: string) {
  const headers: Record<string, string> = {
    ...buildConsumerRuntimeHeaders(sellingPointId),
    "Content-Type": "application/json; charset=UTF-8",
    "x-lang": localStorage.getItem("volqan.consumer.language") || "es",
  };
  return headers;
}

export default function InvoiceRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = String(searchParams.get("orderId") || "").trim();
  const orderSession = useMemo(() => readConsumerOrderSession(orderId), [orderId]);
  const saleId = String(searchParams.get("saleId") || orderSession?.saleId || "").trim();
  const sellingPointId = String(searchParams.get("sellingPointId") || orderSession?.sellingPointId || "").trim();
  const initialPhone = String(searchParams.get("phone") || "").trim();
  const [phone, setPhone] = useState(initialPhone);
  const [smsSending, setSmsSending] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [error, setError] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");

  const invoiceUrl = useMemo(
    () =>
      buildInvoiceAccessUrl({
        orderId,
        saleId,
        sellingPointId,
        marketWebBaseUrl: orderSession?.marketWebBaseUrl,
        phone,
      }),
    [orderId, orderSession?.marketWebBaseUrl, phone, saleId, sellingPointId]
  );

  useEffect(() => {
    if (!invoiceUrl || !orderId || !saleId || !sellingPointId) {
      setQrDataUrl("");
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(invoiceUrl, {
      width: 240,
      margin: 1,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((value: string) => {
        if (!cancelled) setQrDataUrl(value);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceUrl, orderId, saleId, sellingPointId]);

  const handleSendSms = async () => {
    const normalizedPhone = phone.replace(PHONE_SANITIZE_RE, "").trim();
    if (!orderId || !saleId || !sellingPointId || !invoiceUrl) {
      setError(trn("app.consumer.invoice.form_submit_error", "We could not register the invoice request."));
      return;
    }
    if (!normalizedPhone) {
      setError(
        trn("app.consumer.invoice.phone_required", "Enter a mobile number to receive the invoice link.")
      );
      setSmsMessage("");
      return;
    }

    try {
      setSmsSending(true);
      setError("");
      setSmsMessage("");
      await axios.post(
        `${resolveConsumerRuntimeApiBase(sellingPointId)}/pos/sales/${encodeURIComponent(saleId)}/receipt/email`,
        {
          sellingPointId,
          payload: {
            type: "invoice_access_link_v1",
            orderId,
            saleId,
            phone: normalizedPhone,
            invoiceUrl,
            orderCode: orderSession?.friendlyCode || orderId,
            source: "consumer-runtime",
          },
        },
        { headers: buildKioskHeaders(sellingPointId) }
      );
      setSmsMessage(
        trn(
          "app.consumer.invoice.sms_sent",
          "We have queued the SMS with your invoice link."
        )
      );
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.errors?.[0]?.message ||
          trn("app.consumer.invoice.sms_failed", "We could not send the SMS right now.")
      );
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <div className="fullscreen-container-dark">
      <div className="fullscreen-content invoice-request-screen">
        <div className="invoice-request-card">
          <div className="invoice-request-header">
            <div className="invoice-request-kicker">
              {trn("app.consumer.invoice.form_order", "Order")} {orderSession?.friendlyCode || orderId || "-"}
            </div>
            <h1>{trn("app.consumer.invoice.title", "Do you need an invoice?")}</h1>
            <p>
              {trn(
                "app.consumer.invoice.body",
                "Enter your mobile number and we will send you an SMS with a secure link. You can also scan the QR to fill in your fiscal data online."
              )}
            </p>
          </div>

          <div className="invoice-cta-grid">
            <div className="invoice-cta-form">
              <div className="invoice-cta-section-title">
                {trn("app.consumer.invoice.sms_option", "Receive the link by SMS")}
              </div>
              <label className="invoice-cta-label" htmlFor="invoice-phone">
                {trn("app.consumer.invoice.phone", "Mobile number")}
              </label>
              <input
                id="invoice-phone"
                className="form-control form-control-lg"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={trn("app.consumer.invoice.phone_placeholder", "+34 600 123 123")}
                inputMode="tel"
              />
              <div className="invoice-cta-actions">
                <button
                  type="button"
                  className="btn btn-warning btn-lg"
                  disabled={smsSending}
                  onClick={() => void handleSendSms()}
                >
                  <MessageSquareText size={28} />
                  {smsSending
                    ? trn("app.consumer.invoice.sending_sms", "Sending SMS...")
                    : trn("app.consumer.invoice.send_sms", "Send SMS link")}
                </button>
              </div>
              {smsMessage ? <p className="invoice-cta-success">{smsMessage}</p> : null}
              {error ? <p className="invoice-cta-error">{error}</p> : null}
            </div>
            <div className="invoice-cta-qr">
              <div className="invoice-cta-section-title">
                {trn("app.consumer.invoice.qr_option", "Scan the QR")}
              </div>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={trn("app.consumer.invoice.qr_alt", "Invoice request QR")}
                  draggable={false}
                  style={{ pointerEvents: "none", userSelect: "none" }}
                />
              ) : null}
              <span>{trn("app.consumer.invoice.qr_hint", "Scan to complete your invoice online")}</span>
            </div>
          </div>

          <div className="invoice-request-actions">
            <button type="button" className="btn btn-outline-light btn-lg" onClick={() => navigate(-1)}>
              <ArrowLeft size={24} />
              {trn("app.consumer.common.back", "Back")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
